<template>
  <v-app>
    <v-app-bar app flat height="56" color="accent">
      <!-- show the menu button to toggle navigation drawer -->
      <v-app-bar-nav-icon class="d-lg-none" @click="shouldShowDrawer = true" />

      <v-toolbar-title v-if="appBarTitle" class="font-weight-bold">
        {{ appBarTitle }}
      </v-toolbar-title>

      <!-- add invisible space in center -->
      <v-spacer />

      <!-- Show contact form option -->
      <v-icon class="mr-4" @click="triggerContactForm">info</v-icon>

      <!-- show profile and notifications icons at the right -->
      <v-icon class="mr-4">notifications</v-icon>

      <v-menu>
        <!-- show the user icon -->
        <template v-slot:activator="{ on, attrs }">
          <v-icon class="mr-md-6" v-bind="attrs" v-on="on"> person </v-icon>
        </template>

        <!-- show the list items for this user -->
        <v-list width="170px">
          <v-list-item>
            <v-list-item-title>
              {{ availableCredits }} Credits
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="profileValidity">
            <v-list-item-title
              class="cursor-pointer"
              :title="profileValidity.absolute"
            >
              Expires {{ profileValidity.relative }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item @click="shouldShowEditProfileDialog = true">
            <v-list-item-title>
              Edit Profile
            </v-list-item-title>
          </v-list-item>

          <v-list-item @click="shouldShowInfoDialog = true">
            <v-list-item-title>
              Credit Guide
            </v-list-item-title>
          </v-list-item>

          <v-list-item @click="handleLogout">
            <v-list-item-title>
              Logout
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- progress loader -->
      <v-progress-linear
        :active="isLoaderVisible"
        :indeterminate="true"
        absolute
        bottom
        color="primary"
      ></v-progress-linear>
    </v-app-bar>

    <!-- <v-navigation-drawer
      v-model="shouldShowDrawer"
      mini-variant-width="60"
      color="bluegray"
      mini-variant
      app
    > -->
    <v-navigation-drawer
      app
      color="bluegray"
      v-model="shouldShowDrawer"
      class="sidebar"
    >
      <!-- Show the app's logo -->
      <router-link :to="{ name: 'Dashboard' }" class="d-block px-2 mt-4">
        <v-img
          :src="profileLogo ? profileLogo : '/img/icon-primary-white.svg'"
          width="236"
          height="80"
          contain
        />
      </router-link>

      <!-- If we're showing a custom logo, show a text -->
      <div
        v-if="profileLogo && profileData.email !== 'sapto.handriyanto@popsww.com'"
        class="white--text text-center text-subtitle-2 mt-2"
      >
        Powered by <span class="primary--text">Klug</span>
      </div>

      <!-- Show menu links -->
      <v-list
        class="mt-6 mt-lg-8 flex-grow-1"
        dense
        dark
        nav
      >
        <v-list-item
          v-for="link in links"
          :key="link.to.name"
          :to="link.to"
        >
          <v-list-item-icon class="mr-4">
            <v-icon>{{ link.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ link.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="canUseAPI"
          @click="handleRedirect('/docs')"
        >
          <v-list-item-icon class="mr-4">
            <v-icon>
              api
            </v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>
              API Docs
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <!-- Show footer links -->
      <v-list
        dense
        dark
        nav
      >
        <v-list-item :to="{ name: 'FAQ' }">
          <v-list-item-content>
            <v-list-item-title>
              FAQs
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- <v-list-item :to="{ name: 'PrivacyPolicy' }">
          <v-list-item-content>
            <v-list-item-title>Privacy Policy</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :to="{ name: 'Terms' }">
          <v-list-item-content>
            <v-list-item-title>Terms &amp; Conditions</v-list-item-title>
          </v-list-item-content>
        </v-list-item> -->
      </v-list>
    </v-navigation-drawer>

    <v-main class="accent">
      <div class="pa-3 px-md-9 py-md-6">
        <router-view :key="reloadKey" />
      </div>

      <!-- adding components here -->
      <toast-messages />
      <download-bars />

      <!-- The edit user form dialog -->
      <edit-profile-form
        v-if="shouldShowEditProfileDialog"
        @close="shouldShowEditProfileDialog = false"
      />

      <!-- The credit guide info dialog -->
      <v-dialog
        v-model="shouldShowInfoDialog"
        max-width="400"
      >
        <v-card>
          <v-card-title>
            Credit Guide
          </v-card-title>

          <v-card-text>
            <p>
              1 Token in Influencer Discovery unlocks 25 profiles
            </p>
            <p>
              1 Token in Influencer Insight unlocks 1 profile
            </p>
          </v-card-text>

          <v-card-actions>
            <v-spacer />

            <v-btn
              text
              color="primary"
              @click="shouldShowInfoDialog = false"
            >
              Okay
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- anything inside this will be hidden, used for adding and removing temporary elements -->
      <div id="hiddenElement" name="hiddenElement" class="hidden-element"></div>
    </v-main>
  </v-app>
</template>

<script>
import download from "downloadjs"

import dayjs from "@/helpers/dayjs.js"
import { getClientID } from "@/helpers/clientHelper"

const ToastMessages = () => import("@/components/common/ToastMessages.vue")
const DownloadBars = () => import("@/components/common/DownloadBars.vue")
const EditProfileForm = () => import("@/components/profile/Edit.vue")

export default {
  name: "DashboardTemplate",

  components: {
    DownloadBars,
    ToastMessages,
    EditProfileForm
  },

  data: () => ({
    // on each navigation link click, re-render the component
    reloadKey: Symbol(),

    // whether to show or not the sidebar drawer
    shouldShowDrawer: null,

    // let the user edit some basic account information
    shouldShowEditProfileDialog: false,

    // Show the user information about how much credit costs for different services
    shouldShowInfoDialog: false
  }),

  computed: {
    // find the current header/appBar title from the route's meta property
    appBarTitle() {
      return this.$route.meta.appBarTitle || null
    },

    // find whether to show the loader or not from vuex store
    isLoaderVisible() {
      return this.$store.getters["loaders/isVisible"]
    },

    /**
     * Get the logged in user's data object
     *
     * @returns {Object}
     */
    profileData() {
      return this.$store.getters["auth/profile"]
    },

    profileLogo() {
      return this.$store.getters["auth/profileLogo"]
    },

    isWhitelisted() {
      return this.profileData ? this.profileData.is_whitelisted : false
    },

    availableCredits() {
      return this.profileData ? this.profileData.balance / constants.conversionRate : 0
    },

    /**
     * Get the current logged in user's role attribute
     *
     * @returns {String}
     */
    profileRole() {
      return this.profileData ? this.profileData.role : "end-user"
    },

    profileValidity() {
      if (this.profileData && this.profileData.valid_to) {
        return {
          relative: dayjs().to(dayjs.utc(this.profileData.valid_to)),
          absolute: dayjs.utc(this.profileData.valid_to).format("LLL")
        }
      }
      else {
        return null
      }
    },

    canUseAPI() {
      return this.$store.getters["auth/profile"]?.can_use_api || false
    },

    links() {
      const items = [
        {
          icon: "admin_panel_settings",
          color: "primary",
          text: "Administrator",
          condition: this.profileRole === this.constants.model.user.roles.admin,
          to: {
            name: "Admin/Index",
          }
        },
        {
          icon: "group",
          color: "primary",
          text: "Team Manager",
          condition: this.isWhitelisted,
          to: {
            name: "Team/Index"
          }
        },
        {
          icon: "person_search",
          color: "primary",
          text: "Influencer Discovery",
          serviceRequired: this.constants.model.user.allowedServices.influencerDiscovery,
          to: {
            name: "InfluencerDiscovery"
          }
        },
        {
          icon: "insights",
          color: "primary",
          text: "Influencer Insight",
          serviceRequired: this.constants.model.user.allowedServices.influencerInsight,
          to: {
            name: "InfluencerInsight"
          }
        },
        {
          icon: "picture_in_picture",
          color: "primary",
          text: "Audience Overlap",
          serviceRequired: this.constants.model.user.allowedServices.audienceOverlap,
          to: {
            name: "AudienceOverlap"
          }
        },
        {
          icon: "compare",
          color: "primary",
          text: "Influencer Tie Breaker",
          serviceRequired: this.constants.model.user.allowedServices.influencerTieBreaker,
          to: {
            name: "InfluencerTieBreaker"
          }
        },
        {
          icon: "query_stats",
          color: "secondary",
          text: "Paid Collaborations",
          serviceRequired: this.constants.model.user.allowedServices.paidCollaborations,
          to: {
            name: "PaidCollaborations"
          }
        },
        {
          icon: "sentiment_very_satisfied",
          color: "green",
          text: "Social Sentiments",
          serviceRequired: this.constants.model.user.allowedServices.socialSentiments,
          to: {
            name: "SocialSentimentsForm"
          }
        },
        {
          icon: "difference",
          color: "secondary",
          // text: "Paid Collaborations Comparison",
          text: "Paid Comparison",
          serviceRequired: this.constants.model.user.allowedServices.paidComparison,
          to: {
            name: "PaidCollaborationsComparison"
          }
        },
        {
          icon: "receipt_long",
          color: "light-blue",
          // text: "Reports",
          text: "Generated Reports",
          to: {
            name: "Reports"
          }
        },
        // {
        //   icon: "flight_takeoff",
        //   color: "red",
        //   text: "Media Planner",
        //   serviceRequired: this.constants.model.user.allowedServices.mediaPlanner,
        //   to: {
        //     name: "MediaPlannerIndex"
        //   }
        // },
        {
          icon: "travel_explore",
          color: "blue",
          text: "Campaign Planning",
          serviceRequired: this.constants.model.user.allowedServices.campaignPlanning,
          to: {
            name: "CampaignPlanningSearch"
          }
        },
        {
          icon: "campaign",
          color: "green",
          text: "Campaign Reports",
          serviceRequired: this.constants.model.user.allowedServices.campaigns,
          to: {
            name: "CampaignIndex"
          }
        },
      ]

      // now show the menu items by filtering based on conditions
      return items
        // if there's some conditional value (kind of like v-if)
        .filter((item) => typeof item.condition === 'boolean' ? item.condition : true)
        // if the required service is allowed for this user
        .filter((item) => item.serviceRequired ? this.$store.getters["auth/isServiceAllowed"](item.serviceRequired) : true)
    }
  },

  methods: {
    handleLogout() {
      window.dispatchEvent(new CustomEvent("triggerLogout", {
        detail: {
          module: "dashboard",
          origin: "templates/dashboard/handleLogout"
        }
      }))
    },

    triggerContactForm() {
      window.dispatchEvent(new CustomEvent("showContactForm", { detail:null }))
    },

    handleLinkClick() {
      this.reloadKey = Symbol()

      // reset the drawer state
      this.shouldShowDrawer = null
    },

    handleRedirect(path) {
      window.location.href = path
    },

    /**
     * Show or hide the download progress bars
     *
     * @param {Object} e
     * @returns {void}
     */
    handleDownloadProgress(e) {
      // Check if the event data has a toast message for us
      if (e.localData.toast) {
        this.$store.dispatch("toasts/add", {
          text: e.localData.toast,
          timeout: 5000
        })
      }

      // If the download progress is now complete
      if (e.localData.shouldEnd) {
        // Remove it
        this.$store.dispatch('downloads/remove', e.localData)
      }
      // Otherwise show the progress bar
      else {
        this.$store.dispatch('downloads/add', e.localData)
      }
    },

    /**
     * Whenever the status changes, simply let social sentiments history page know that we're done
     *
     * @param {Object} e
     * @returns {void}
     */
    handleBulkExportResult(e) {
      window.dispatchEvent(new CustomEvent("BulkExportResult", {
        detail: {
          origin: "templates/dashboard/handleBulkExportResult",
          data: e
        }
      }))
    },

    /**
     * Whenever the status changes, simply let social sentiments history page know that we're done
     *
     * @param {Object} e
     * @returns {void}
     */
    handleSocialSentimentProgress(e) {
      window.dispatchEvent(new CustomEvent("SocialSentimentStatusChange", {
        detail: {
          origin: "templates/dashboard/handleSocialSentimentProgress",
          data: e
        }
      }))
    },

    /**
     * Fetch the file from API and download it using JavaScript to the user's disk
     *
     * @param {Object} e
     * @returns {void}
     */
    async handleReadyToDownload(e) {
      // Dispatch a window event for anyone to listen to it
      window.dispatchEvent(new CustomEvent("ReadyToDownload", {
        detail: {
          origin: "templates/dashboard/mounted/echo/ReadyToDownload",
          data: e.temporaryFile
        }
      }))

      // Now check if there's a client ID present in this response
      const clientId = e.temporaryFile?.misc?.clientId || null

      // If the client ID matches or if there's no clientId, then download it now
      if (getClientID() === clientId || clientId === null) {
        // Now trigger the download for this file
        try {
          const response = await axios({
            url: "/api/temporary-files/" + e.temporaryFile.id,
            method: "POST",
            responseType: 'arraybuffer', // since we'd be downloading it
            onDownloadProgress: (event) => {
              // The download id to be used
              const downloadId = "download-progress-" + e.temporaryFile.id

              // If the download progress is now complete
              if (event.loaded === event.total) {
                // Remove it
                this.$store.dispatch('downloads/remove', { downloadId })
              }
              // Otherwise show the progress bar
              else {
                this.$store.dispatch('downloads/add', {
                  downloadId,
                  downloadText: "Downloading File",
                  downloadSubText: `${(event.loaded / (10**6)).toFixed(2)}/${(event.total / (10**6)).toFixed(2)} MB`,
                  progress: event.loaded,
                  total: event.total
                })
              }
            }
          })

          // Now force this response to be downloaded
          download(response.data, e.temporaryFile.download_name)
        } catch (error) {
          logger({ type: "DownloadFailed", error })

          this.$store.dispatch("toasts/add", { text: "Download failed, please try again." })
        }
      }
    },
  },

  created() {
    // console.log("Mounted and waiting for profile data", Date.now())

    // console.log(this.handleDownloadProgress)
    // console.log(this.handleReadyToDownload)

    window.addEventListener("fetchedProfile", (e) => {
      // console.log("Received profile data", Date.now(), e.detail)

      // Let us know when this is done
      if (e.detail.data) {
        // Start listening to the global laravel echo connection
        // Only register the event listeners once!
        if (window.HAS_COMMON_PUSHER_BEEN_SET) {
          return null
        }

        // Make sure we're not listening to the same events multiple times
        window.HAS_COMMON_PUSHER_BEEN_SET = "YES"

        // console.log("Registering echo events now", Date.now())

        window.echo
          // Connect to the logged in user's events
          .private(`users.${e.detail.data.id}`)
          // Listen to all the download progress events
          .listen('DownloadProgress', this.handleDownloadProgress)
          // Listen to the download ready responses
          .listen('ReadyToDownload', this.handleReadyToDownload)
          // Listen to events when bulk insight export is done
          .listen('BulkExportResult', this.handleBulkExportResult)
          // Listen to events whenever the status changes
          .listen('SocialSentimentProgress', this.handleSocialSentimentProgress)
      }
    })
  },

  beforeDestroy() {
    if (this.profileData) {
      // Disconnect from the pusher channel
      window.echo.leave(`users.${this.profileData.id}`)
    }
  }
}
</script>

<style lang="stylus" scoped>
.brand-logo
  object-fit contain
  height 30px
  margin-right 1em

.hidden-element
  position fixed
  z-index -20000
  left 0
  top 0
  width 100%
  height auto
</style>

<style lang="stylus">
.sidebar
  .v-navigation-drawer__content
    display flex
    flex-direction column

    .v-list
      &:last-child
        .v-list-item
          min-height 18px

          &__content
            padding 4px 0

  // To fix the sidebar issue
  ::-webkit-scrollbar
    width 4px

  /* Track */
  ::-webkit-scrollbar-track
    background #f4f6f8

  /* Handle */
  ::-webkit-scrollbar-thumb
    background #373b56

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover
    background #f05d56
</style>
